import React from 'react';
import { IconLoyalty } from '@noths/polaris-client-ribbons-design-system';

import type { DecoratedDeliveryOption } from 'src/redux/delivery/types';
import { getCutoffMessage } from '../../utils/getCutoffMessage';
import { getDateNDaysFromNow } from '../../utils/getDateNDaysFromNow';
import { getDeliveryOptionText } from '../../utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from '../../utils/getFormattedDeliveryDate';
import { getFormattedPrice } from '../../utils/getFormattedPrice';
import type { ArrivesBy } from '../DeliveryBlock/types';
import * as styles from './DeliveryOptions.styles';

interface DeliveryOptionsProps {
  arrivesBy?: ArrivesBy;
  deliveryOptions: DecoratedDeliveryOption[] | [];
  partnerName: string;
}

export const DeliveryOptions = ({
  arrivesBy,
  deliveryOptions,
  partnerName,
}: DeliveryOptionsProps) => {
  if (!deliveryOptions.length) {
    return null;
  }

  const hasFreeOption = deliveryOptions.some((option) => Number(option.price.amount) === 0);

  return (
    <ul css={[styles.deliveryOptionsList, styles.fadeIn]}>
      {deliveryOptions.map(
        ({
          cutoffDateTime,
          date,
          exactDate,
          max,
          min,
          optionCode,
          partnerFreeDeliveryThresholdPrice,
          price: { amount, currencyCode },
        }) => {
          const optionPrice = Number(amount);
          const cutoffMessage = getCutoffMessage(cutoffDateTime);
          const formattedPrice = getFormattedPrice(optionPrice, currencyCode);
          let showArrivesBy = false;

          if (arrivesBy) {
            const optionDate = exactDate ? date : max ? getDateNDaysFromNow(max) : null;

            if (optionDate && optionDate <= arrivesBy.date) {
              showArrivesBy = true;
            }
          }

          return (
            <li css={styles.item} key={optionCode}>
              <div css={styles.optionInfo}>
                <span>
                  <h6 css={styles.optionHeading}>
                    {exactDate && date ? (
                      <b>
                        {optionCode === 'SATURDAY' && 'On '}
                        <time dateTime={date}>{getFormattedDeliveryDate(date)}</time>
                      </b>
                    ) : (
                      `${min}-${max} days`
                    )}
                    {showArrivesBy ? (
                      <span>
                        {' '}
                        · Arrives in time for{' '}
                        <time dateTime={arrivesBy!.date}>{arrivesBy!.name}</time>
                      </span>
                    ) : null}
                  </h6>
                  <p css={styles.optionBody}>
                    {cutoffMessage ? `Order by ${cutoffMessage} · ` : ''}
                    {getDeliveryOptionText(optionCode)}
                  </p>
                </span>
                <b data-price={optionPrice}>{formattedPrice}</b>
              </div>
              {partnerFreeDeliveryThresholdPrice && !hasFreeOption && (
                <p css={styles.freePartnerDelivery}>
                  <span>
                    <IconLoyalty color={'var(--functional-green-base)'} height={16} width={16} />
                  </span>
                  <span>
                    <b>Free standard delivery</b> when you spend over{' '}
                    {getFormattedPrice(
                      Number(partnerFreeDeliveryThresholdPrice.amount),
                      partnerFreeDeliveryThresholdPrice.currencyCode,
                    )}{' '}
                    with {partnerName}
                  </span>
                </p>
              )}
            </li>
          );
        },
      )}
    </ul>
  );
};
